import { saveToIndexedDB, getFromIndexedDB } from '../indexedDB';
import { normalizeIconObject } from './iconEntries';
import { CURRENT_CUSTOMIZER_VERSION } from './designState';
// This file contains helpers that keep new Customizer Versions backward-compatible
// with old design states (localStorage & indexedDB) and
// migrates old design states to the latest to the latest design schema

const migrateDesignFromV1ToV11 = (design = {}) => {
  const migratedKeycapData = Array.isArray(design.keycapData)
    ? design.keycapData.map((row) =>
        Array.isArray(row)
          ? row.map((keycap) => {
              if (!keycap || keycap.isSpacer || !keycap.icons) {
                return keycap; // skip empty keycaps, spacer keycaps and keycaps without icons
              }

              const migratedIcons = Object.entries(keycap.icons).reduce(
                (acc, [iconKey, iconObj]) => {
                  acc[iconKey] = normalizeIconObject(iconObj); // normalize the icon object to make old icon entries compatible with the new icon structure
                  return acc;
                },
                {}
              );

              return {
                ...keycap,
                icons: migratedIcons, // set the migrated icons back into the keycap
              };
            })
          : row
      )
    : design.keycapData; // keep keycapData unchanged if it is missing or not an array

  return {
    ...design,
    keycapData: migratedKeycapData,
    iconEditor: {
      ...(design.iconEditor || {}),
      activeIconLibraryTab:
        design.iconEditor?.activeIconLibraryTab === 'uploaded'
          ? 'uploaded'
          : 'built-in',
    }, // add the activeIconLibraryTab field to the iconEditor
    metadata: {
      ...design.metadata,
      customizerVersion: '1.1', // update the design to the new customizer version after the migration
    },
  };
}; // migrates a design from customizer version 1.0 to 1.1

const CUSTOMIZER_VERSION_MIGRATIONS = {
  '1.0': migrateDesignFromV1ToV11, // migrate designs from version 1.0 to version 1.1
}; // maps each customizer version to the migration that updates it to the next version

export const migrateDesignIfNeeded = (design) => {
  let migratedDesign = design; // start with the provided design
  let version = migratedDesign?.metadata?.customizerVersion || '1.0'; // treat designs without a customizerVersion as version 1.0

  while (version !== CURRENT_CUSTOMIZER_VERSION) {
    const migration = CUSTOMIZER_VERSION_MIGRATIONS[version]; // get the migration for the current customizer version

    if (!migration) {
      console.warn(`No migration found for customizer version ${version}`);
      return migratedDesign; // stop migrating if no migration exists for the current version
    }

    migratedDesign = migration(migratedDesign); // apply the migration for the current customizer version
    version = migratedDesign?.metadata?.customizerVersion; // get the updated customizer version after the migration
  }

  return migratedDesign; // return the original or migrated design
}; // migrates a design to the current customizer version if needed

export const getMigratedDesignAssetFromIndexedDB = async (uniqueCartId) => {
  const asset = await getFromIndexedDB(uniqueCartId); // fetch the cart asset from IndexedDB

  if (!asset || !asset.design) {
    return asset; // return as-is so the caller can handle missing or invalid assets
  }

  const rawDesign = asset.design; // get the design stored inside the IndexedDB asset
  const previousVersion = rawDesign?.metadata?.customizerVersion || '1.0'; // treat designs without a customizerVersion as version 1.0
  const migratedDesign = migrateDesignIfNeeded(rawDesign); // migrate the IndexedDB design to the latest customizerVersion
  const nextVersion =
    migratedDesign?.metadata?.customizerVersion || previousVersion; // get the version after migration

  if (previousVersion !== nextVersion) {
    await saveToIndexedDB({
      ...asset,
      uniqueCartId,
      design: migratedDesign,
    }); // save the migrated design back to IndexedDB if its version changed
  }

  return {
    ...asset,
    design: migratedDesign,
  }; // return the asset with the migrated design
}; // loads an IndexedDB cart asset and migrates its design before use
